import type { TilePosition } from './GameState';

export type InputType = 'tile' | 'entity' | 'text';

export interface TileInput {
  kind: 'tile';
  x: number;
  y: number;
}

export interface EntityInput {
  kind: 'entity';
  entityId: string;
  x: number;
  y: number;
}

export interface TextInput {
  kind: 'text';
  value: string;
}

export type Input = TileInput | EntityInput | TextInput;

export interface SpatialSelection {
  type: 'tile' | 'entity';
  position: TilePosition;
  entityId?: string;
}

const INPUT_TYPES = new Set<InputType>(['tile', 'entity', 'text']);

export function normalizeInputTypes(rawInputs?: string | string[] | InputType[]): InputType[] {
  if (!rawInputs) {
    return [];
  }

  const values = Array.isArray(rawInputs)
    ? rawInputs
    : parseRawStringInputs(rawInputs);

  return values
    .map((value) => value.trim().toLowerCase())
    .filter((value): value is InputType => INPUT_TYPES.has(value as InputType));
}

function parseRawStringInputs(rawInputs: string): string[] {
  try {
    const parsed = JSON.parse(rawInputs) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((entry): entry is string => typeof entry === 'string');
    }
    if (typeof parsed === 'string') {
      return [parsed];
    }
  } catch {
    // Treat raw string as a single descriptor fallback.
  }
  return rawInputs.trim().length ? [rawInputs] : [];
}

export function buildInputsFromSelection(
  inputTypes: InputType[],
  selections: SpatialSelection[] = [],
  fixedInputs: Input[] = []
): Input[] {
  if (inputTypes.length === 0) {
    return [];
  }

  const inputs: Input[] = [];
  let spatialInputIndex = 0;
  let textInputIndex = 0;

  for (const inputType of inputTypes) {
    if (inputType === 'tile') {
      const selection = selections[spatialInputIndex];
      spatialInputIndex += 1;
      if (selection) {
        inputs.push({ kind: 'tile', x: selection.position.x, y: selection.position.y });
      }
      continue;
    }

    if (inputType === 'entity') {
      const selection = selections[spatialInputIndex];
      spatialInputIndex += 1;
      if (selection?.entityId) {
        inputs.push({
          kind: 'entity',
          entityId: selection.entityId,
          x: selection.position.x,
          y: selection.position.y,
        });
      }
      continue;
    }

    if (inputType === 'text') {
      const textInput = fixedInputs
        .filter((input): input is TextInput => input.kind === 'text')
        [textInputIndex];
      textInputIndex += 1;
      inputs.push({
        kind: 'text',
        value: textInput?.value ?? '',
      });
    }
  }

  return inputs;
}
